/* eslint no-param-reassign: 0 */
import { cloneDeep, get } from 'lodash';
import { series } from 'async';
import SystemUserWebAuthnApi from 'client/api/v2/SystemUserWebAuthnApi';
import createModel from 'client/store/modules/models/createModel';

const findWebAuthnKeyIndex = (keys, key) => keys.findIndex(k => k.id === key.id);

// NOTE: The id is actually the ID of the SystemUser who has registered WebAuthn keys.
export const getDefaultItem = () => ({
  id: '',
  displayName: '',
  username: '',
  webAuthnKeys: [],
});

const model = createModel(getDefaultItem, 'id', SystemUserWebAuthnApi);

const allCredentials = (state) => get(state, 'item.webAuthnKeys');

const legacyCredentials = (state) => {
  const credentials = allCredentials(state);
  return credentials.filter(({ attachment }) => !attachment);
};

const securityKeyCredentials = (state) => {
  const credentials = allCredentials(state);
  return credentials.filter(({ attachment }) => attachment === 'cross-platform');
};

const deviceBiometricCredentials = (state) => {
  const credentials = allCredentials(state);
  return credentials.filter(({ attachment }) => attachment === 'platform');
};

/**
 * This Vuex store model is used to manage the WebAuthn Keys of an existing system user.
 */
export default {
  namespaced: true,

  state: {
    ...model.state,
  },

  actions: {
    ...model.actions,

    addWebAuthnKey({ commit }, webAuthnKey) {
      commit('ADD_WEB_AUTHN_KEY', cloneDeep(webAuthnKey));
      commit('ADD_PREVIOUS_WEB_AUTHN_KEY', cloneDeep(webAuthnKey));
    },

    deleteWebAuthnKey({ commit }, webAuthnKey) {
      commit('DELETE_WEB_AUTHN_KEY', webAuthnKey);
    },

    fetchItem(context) {
      const { id } = context.getters;

      return SystemUserWebAuthnApi
        .fetchAll(id)
        .then((webuAuthnKeys) => {
          context.commit('SET_ITEM', { webAuthnKeys: cloneDeep(webuAuthnKeys) });
          context.commit('SET_PREVIOUS_ITEM', { webAuthnKeys: cloneDeep(webuAuthnKeys) });
        });
    },

    fetchPublicKeyCredentialsCreationOptions(context) {
      const { id } = context.getters;
      return SystemUserWebAuthnApi.startRegistration(id);
    },

    registerPublicKeyCredential(context, { token, name, publicKeyCredential }) {
      const { id } = context.getters;
      return SystemUserWebAuthnApi.finishRegistration(id, token, name, publicKeyCredential);
    },

    saveWebAuthnKeys({ dispatch, getters, state }) {
      const { id } = getters;
      const keys = state.item.webAuthnKeys;
      const previousKeys = state.previousItem.webAuthnKeys;

      const deletedKeys = previousKeys.reduce((acc, previousKey) => {
        const index = findWebAuthnKeyIndex(keys, previousKey);

        if (index === -1) {
          acc.push(previousKey);
        }

        return acc;
      }, []);

      const updatedKeys = keys.reduce((acc, key) => {
        const index = findWebAuthnKeyIndex(previousKeys, key);
        const previousKey = previousKeys[index];

        if (key.enabled !== previousKey.enabled || key.name !== previousKey.name) {
          acc.push(key);
        }

        return acc;
      }, []);

      const tasks = [];

      deletedKeys.forEach((deletedKey) => {
        tasks.push((cb) => {
          SystemUserWebAuthnApi
            .delete(id, deletedKey)
            .then(() => cb())
            .catch(err => cb(err));
        });
      });

      updatedKeys.forEach((updatedKey) => {
        tasks.push((cb) => {
          SystemUserWebAuthnApi
            .update(id, updatedKey)
            .then(() => cb())
            .catch(err => cb(err));
        });
      });

      return new Promise((resolve, reject) => {
        if (tasks.length === 0) {
          return resolve();
        }

        // NOTE: The series() method from the async library is used here to ensure that the API
        // requests are made one at a time to avoid a race condition while local storage is used by
        // SystemUserWebAuthnApi to save the keys! When that's no longer neessary, Promise.all()
        // can likely be used instead if the API doesn't exhibit a similar race condition.
        return series(tasks, (err) => {
          if (err) {
            reject(err);
          } else {
            // Fetch a fresh copy of the WebAuthn keys so that the copy of the current and previous
            // keys in the state are the same and reflect the current state of the WebAuthn keys on
            // the server.
            dispatch('fetchItem')
              .then(() => resolve())
              .catch(err2 => reject(err2));
          }
        });
      });
    },

    /**
     * @todo WebAuthn keys are currently updated in the UI one field at a time. i.e. The display
     * name is changed, and then the enabled status is changed. As a result, this action has _not_
     * been designed to accommodate updating multiple fields on the key at once - simply because
     * that's not yet necessary. If/when it is necessary to update multiple fields of a key at
     * once this method can be updated accordingly.
     */
    updateWebAuthnKey({ commit }, { webAuthnKey, key, value }) {
      commit('UPDATE_WEB_AUTHN_KEY', { webAuthnKey, key, value });
    },
  },

  getters: {
    ...model.getters,
    deviceBiometricCredentials,
    legacyCredentials,
    securityKeyCredentials,
  },

  mutations: {
    ...model.mutations,

    ADD_PREVIOUS_WEB_AUTHN_KEY(state, webAuthnKey) {
      state.previousItem.webAuthnKeys.push(webAuthnKey);
    },

    ADD_WEB_AUTHN_KEY(state, webAuthnKey) {
      state.item.webAuthnKeys.push(webAuthnKey);
    },

    DELETE_WEB_AUTHN_KEY(state, webAuthnKey) {
      const { webAuthnKeys } = state.item;
      const index = findWebAuthnKeyIndex(webAuthnKeys, webAuthnKey);
      webAuthnKeys.splice(index, 1);
    },

    UPDATE_WEB_AUTHN_KEY(state, { webAuthnKey, key, value }) {
      const { webAuthnKeys } = state.item;
      const index = findWebAuthnKeyIndex(webAuthnKeys, webAuthnKey);
      webAuthnKeys[index][key] = value;
    },
  },
};
